import { t } from '../i18n/strings'
import {
  actorsMatch,
  formatCardsPretty,
  formatCardPretty,
  formatHolePro,
} from './notation'
import {
  deriveHandFlow,
  handEndsOnePlayerLeft,
  handEndsMemoryCut,
} from './handFlow'
import { sanitizeEnding } from './endingRecord'
import { formatPotDisplay, type PotDisplayParts } from './potLabel'
import type { Card, HandState, Rank, Street, StreetAction, Suit } from './types'
import {
  buildLineSegments,
  formatBlindsNotation,
  formatLineText,
  formatStackNotation,
  lineSettingsText,
  type LineLabels,
  type LineSegment,
} from './lineText'

const RANKS: Rank[] = [
  'A',
  'K',
  'Q',
  'J',
  'T',
  '9',
  '8',
  '7',
  '6',
  '5',
  '4',
  '3',
  '2',
]

export const SUITS: Suit[] = ['s', 'h', 'd', 'c']

export const SUIT_SYMBOL: Record<Suit, string> = {
  s: '♠',
  h: '♥',
  d: '♦',
  c: '♣',
}

/** Locale-aware street labels (call each render). */
export function streetLabel(street: Street): string {
  return t().street[street]
}

export function actionLabel(type: string): string {
  const map = t().actionType as Record<string, string>
  return map[type] ?? type
}

export function getSteps(): { id: 'position' | 'cards' | 'actions' | 'summary'; label: string }[] {
  const s = t().step
  // Softwise M10b: board is asked inline between streets on the Line step.
  // Softwise M10c: the first question is the seat — setup is a side screen
  // (opened from the defaults segment), not a step to pass through.
  return [
    { id: 'position', label: s.position },
    { id: 'cards', label: s.cards },
    { id: 'actions', label: s.actions },
    { id: 'summary', label: s.summary },
  ]
}

/** Softwise M10b: localized labels for the single line generator. */
export function lineLabels(): LineLabels {
  const ui = t()
  return { street: ui.street, action: ui.actionType }
}

/**
 * Softwise M10b: THE line text — input stacked line, Share card, Copy and PNG
 * all print this so they can never disagree.
 */
export function handLineText(state: HandState): string {
  return formatLineText(state, lineLabels())
}

export function handLineSegments(
  state: HandState,
  opts: { seatPicked?: boolean } = {},
): LineSegment[] {
  return buildLineSegments(state, lineLabels(), opts)
}

/** Softwise M10c: quiet defaults segment (`6명 · 0.5/1 · 100bb`) — lineText. */
export function handSettingsText(state: HandState): string {
  return lineSettingsText(state, t().playersUnit, t().stackChipsUnit)
}

/**
 * Softwise M10d: the single share-card settings line — `6명 · 0.5/1 · BTN · 100bb`.
 * Used by the Share card and the PNG so the receiver sees one notation.
 */
export function handShareMetaHead(state: HandState): string {
  const ui = t()
  return [
    ui.tableLine(state.playerCount, formatBlindsNotation(state)),
    formatSeatCode(state.heroPosition, state.playerCount),
    formatStackNotation(state, ui.stackChipsUnit),
  ].join(' · ')
}

/** Softwise M10d: the single stack notation (`100bb`) — lineText formatter. */
export function handStackText(state: HandState): string {
  return formatStackNotation(state, t().stackChipsUnit)
}

/** Softwise M10d: the single blinds notation (`0.5/1`) — lineText formatter. */
export function handBlindsText(state: HandState): string {
  return formatBlindsNotation(state)
}

/** @deprecated use streetLabel / getSteps — frozen Korean snapshots removed. */
export const STREET_LABEL: Record<Street, string> = {
  get preflop() {
    return t().street.preflop
  },
  get flop() {
    return t().street.flop
  },
  get turn() {
    return t().street.turn
  },
  get river() {
    return t().street.river
  },
}

export const ACTION_LABEL: Record<string, string> = new Proxy(
  {} as Record<string, string>,
  {
    get(_target, prop) {
      if (typeof prop !== 'string') return undefined
      return actionLabel(prop)
    },
  },
)

/** HU display: BTN acts as SB → show BTN/SB. */
export function formatSeatCode(pos: string, playerCount: number): string {
  if (playerCount === 2 && pos === "BTN") return "BTN/SB"
  return pos
}

/**
 * Compact oval seat labels — fit 44px seats without ellipsis.
 * Full `formatSeatCode` / hints stay on title + aria.
 */
export function formatTableSeatLabel(pos: string, playerCount: number): string {
  if (playerCount === 2 && pos === 'BTN') return 'B/S'
  if (pos === 'UTG+1') return 'U+1'
  if (pos === 'MP+1') return 'M+1'
  return pos
}

export function positionHint(pos: string, playerCount?: number): string {
  if (playerCount === 2 && pos === 'BTN') {
    return t().positionHintHuBtn
  }
  return t().positionHintMap[pos] ?? ''
}

/**
 * Accessible / toast full seat name: `UTG — Under the gun`.
 * Short codes stay on chips; this pairs code + positionHint.
 */
export function formatSeatFullAccess(pos: string, playerCount: number): string {
  const code = formatSeatCode(pos, playerCount)
  const hint = positionHint(pos, playerCount)
  if (hint && hint !== code) return `${code} — ${hint}`
  return hint || code
}

/** Short seat-order line for position step (HU clarifies BTN≈SB). */
export function seatOrderLine(playerCount: number): string {
  const positions = getPositions(playerCount)
  if (playerCount === 2) return t().seatOrderHu
  return t().seatOrder(positions.join(' → '))
}


function middlePositions(count: number): string[] {
  const n = Math.max(0, count - 3)
  if (n === 0) return []
  if (n === 1) return ['UTG']
  if (n === 2) return ['UTG', 'CO']
  if (n === 3) return ['UTG', 'MP', 'CO']
  if (n === 4) return ['UTG', 'MP', 'HJ', 'CO']
  if (n === 5) return ['UTG', 'UTG+1', 'MP', 'HJ', 'CO']
  const extras = ['UTG', 'UTG+1', 'MP', 'MP+1', 'HJ', 'CO']
  return extras.slice(0, n)
}

export function getPositions(playerCount: number): string[] {
  const count = Math.min(9, Math.max(2, playerCount))
  if (count === 2) return ['BTN', 'BB']
  return [...middlePositions(count), 'BTN', 'SB', 'BB']
}

export function parseCard(card: Card): { rank: Rank; suit: Suit } {
  return { rank: card[0] as Rank, suit: card[1] as Suit }
}

export function formatCard(card: Card): string {
  return formatCardPretty(card)
}

export function formatCards(cards: (Card | null)[]): string {
  return formatCardsPretty(cards)
}

export function usedCards(state: HandState): Set<Card> {
  const used = new Set<Card>()
  for (const c of state.holeCards) if (c) used.add(c)
  for (const holes of Object.values(state.knownHoles ?? {})) {
    if (!holes) continue
    for (const c of holes) if (c) used.add(c)
  }
  for (const c of state.board.flop) if (c) used.add(c)
  if (state.board.turn) used.add(state.board.turn)
  if (state.board.river) used.add(state.board.river)
  return used
}

export function chipsToBb(chips: number, bigBlind: number): number {
  if (!Number.isFinite(chips) || chips < 0) return 0
  if (!Number.isFinite(bigBlind) || bigBlind <= 0) return chips
  const bb = chips / bigBlind
  return Math.round(bb * 100) / 100
}

export function bbToChips(bb: number, bigBlind: number): number {
  if (!Number.isFinite(bb) || bb < 0) return 0
  if (!Number.isFinite(bigBlind) || bigBlind <= 0) return bb
  return Math.round(bb * bigBlind * 100) / 100
}

export function createInitialState(): HandState {
  const bigBlind = 1
  const heroStackBb = 100
  return {
    playerCount: 6,
    smallBlind: 0.5,
    bigBlind,
    heroStackBb,
    heroStackChips: bbToChips(heroStackBb, bigBlind),
    stackUnit: 'bb',
    heroPosition: 'BTN',
    holeCards: [null, null],
    knownHoles: {},
    board: {
      flop: [null, null, null],
      turn: null,
      river: null,
    },
    actions: [],
    takeaway: '',
  }
}

/** True when the hand differs from a fresh default (draft / in-progress). */
export function handHasDraftProgress(hand: HandState): boolean {
  if (hand.actions.length > 0) return true
  if ((hand.unrecordedSeats ?? []).length > 0) return true
  if (hand.memoryCutAt != null) return true
  if (hand.holeCards.some(Boolean)) return true
  if (hand.board.flop.some(Boolean) || hand.board.turn || hand.board.river) {
    return true
  }
  for (const holes of Object.values(hand.knownHoles ?? {})) {
    if (holes?.[0] || holes?.[1]) return true
  }
  if (hand.playerCount !== 6) return true
  if (hand.heroPosition !== 'BTN') return true
  if (hand.stackUnit !== 'bb') return true
  if (hand.heroStackBb !== 100) return true
  if (hand.smallBlind !== 0.5 || hand.bigBlind !== 1) return true
  if (hand.ante != null && Number.isFinite(hand.ante)) return true
  if (hand.effectiveStackBb != null && Number.isFinite(hand.effectiveStackBb)) {
    return true
  }
  if (hand.potBb != null && Number.isFinite(hand.potBb)) return true
  if (hand.takeaway?.trim()) return true
  return false
}

/**
 * Multiway PF helper: seats that act before hero (getPositions order).
 * Empty when HU or hero is first to act.
 */
export function seatsBeforeHero(hand: HandState): string[] {
  const positions = getPositions(hand.playerCount)
  const heroIdx = positions.indexOf(hand.heroPosition)
  if (heroIdx <= 0) return []
  return positions.slice(0, heroIdx)
}

export type FoldToHeroPlan = {
  /** Seats to append as PF folds (no prior PF action). */
  toFold: string[]
  /** True when a seat before hero already has a non-fold PF action. */
  blocked: boolean
}

/**
 * Plan safe "folds to hero" PF macros — never invents folds after aggression.
 */
export function planFoldToHero(hand: HandState): FoldToHeroPlan {
  const before = seatsBeforeHero(hand)
  if (before.length === 0) return { toFold: [], blocked: false }
  const pf = hand.actions.filter((a) => a.street === 'preflop')
  const toFold: string[] = []
  for (const seat of before) {
    const seatActs = pf.filter((a) =>
      actorsMatch(a.actor, seat, hand.heroPosition),
    )
    if (seatActs.some((a) => a.type !== 'fold')) {
      return { toFold: [], blocked: true }
    }
    if (!seatActs.some((a) => a.type === 'fold')) toFold.push(seat)
  }
  return { toFold, blocked: false }
}

/** Seats strictly after `fromPos` and strictly before `toPos` in positions order. */
export function seatsBetweenInOrder(
  positions: string[],
  fromPos: string,
  toPos: string,
): string[] {
  const fromIdx = positions.indexOf(fromPos)
  const toIdx = positions.indexOf(toPos)
  if (fromIdx < 0 || toIdx < 0 || toIdx <= fromIdx + 1) return []
  return positions.slice(fromIdx + 1, toIdx)
}

export type OpenThenCallStep =
  | { actor: string; type: 'fold' }
  | { actor: string; type: 'raise'; size: string }
  | { actor: string; type: 'call' }

export type OpenThenCallPlan = {
  /** PF steps still needed (folds / hero raise / call), in chronological order. */
  toAdd: OpenThenCallStep[]
  /** True when another open exists, or a seat that should fold already acted non-fold. */
  blocked: boolean
}

function isPfAggressionType(type: string): boolean {
  return type === 'raise' || type === 'bet' || type === 'allin'
}

/**
 * Plan chronologically honest "Open then call": folds before hero, hero raise,
 * folds between hero and call seat, then villain call. HU stays raise+call only.
 */
export function planOpenThenCall(
  hand: HandState,
  callSeat: string,
  raiseSize: string,
): OpenThenCallPlan {
  const positions = getPositions(hand.playerCount)
  const before = seatsBeforeHero(hand)
  const between = seatsBetweenInOrder(positions, hand.heroPosition, callSeat)
  const pf = hand.actions.filter((a) => a.street === 'preflop')

  const heroHasAgg = pf.some(
    (a) =>
      isPfAggressionType(a.type) &&
      actorsMatch(a.actor, 'Hero', hand.heroPosition),
  )
  const otherHasAgg = pf.some(
    (a) =>
      isPfAggressionType(a.type) &&
      !actorsMatch(a.actor, 'Hero', hand.heroPosition),
  )
  if (!heroHasAgg && otherHasAgg) {
    return { toAdd: [], blocked: true }
  }

  const foldSeats = [...before, ...between]
  for (const seat of foldSeats) {
    const seatActs = pf.filter((a) =>
      actorsMatch(a.actor, seat, hand.heroPosition),
    )
    if (seatActs.some((a) => a.type !== 'fold')) {
      return { toAdd: [], blocked: true }
    }
  }

  const toAdd: OpenThenCallStep[] = []
  for (const seat of before) {
    const hasFold = pf.some(
      (a) =>
        a.type === 'fold' && actorsMatch(a.actor, seat, hand.heroPosition),
    )
    if (!hasFold) toAdd.push({ actor: seat, type: 'fold' })
  }
  if (!heroHasAgg) {
    toAdd.push({ actor: 'Hero', type: 'raise', size: raiseSize })
  }
  for (const seat of between) {
    const hasFold = pf.some(
      (a) =>
        a.type === 'fold' && actorsMatch(a.actor, seat, hand.heroPosition),
    )
    if (!hasFold) toAdd.push({ actor: seat, type: 'fold' })
  }
  const callExists = pf.some(
    (a) =>
      a.type === 'call' && actorsMatch(a.actor, callSeat, hand.heroPosition),
  )
  if (!callExists) {
    toAdd.push({ actor: callSeat, type: 'call' })
  }
  return { toAdd, blocked: false }
}

/**
 * Full chronological open-then-call PF sequence: folds before hero, hero raise,
 * folds between hero and call seat, then call. Reuses matching existing PF
 * StreetActions when present; otherwise creates new ones with uid().
 */
export function buildOpenThenCallOrderedPf(
  hand: HandState,
  callSeat: string,
  raiseSize: string,
): StreetAction[] {
  const positions = getPositions(hand.playerCount)
  const before = seatsBeforeHero(hand)
  const between = seatsBetweenInOrder(positions, hand.heroPosition, callSeat)
  const pf = hand.actions.filter((a) => a.street === 'preflop')
  const used = new Set<string>()

  function takeMatching(
    pred: (a: StreetAction) => boolean,
  ): StreetAction | undefined {
    const found = pf.find((a) => !used.has(a.id) && pred(a))
    if (found) used.add(found.id)
    return found
  }

  const ordered: StreetAction[] = []

  for (const seat of before) {
    const existing = takeMatching(
      (a) =>
        a.type === 'fold' && actorsMatch(a.actor, seat, hand.heroPosition),
    )
    ordered.push(
      existing ?? {
        id: uid(),
        street: 'preflop',
        actor: seat,
        type: 'fold',
      },
    )
  }

  {
    const existing = takeMatching(
      (a) =>
        isPfAggressionType(a.type) &&
        actorsMatch(a.actor, 'Hero', hand.heroPosition),
    )
    ordered.push(
      existing ?? {
        id: uid(),
        street: 'preflop',
        actor: 'Hero',
        type: 'raise',
        size: raiseSize,
      },
    )
  }

  for (const seat of between) {
    const existing = takeMatching(
      (a) =>
        a.type === 'fold' && actorsMatch(a.actor, seat, hand.heroPosition),
    )
    ordered.push(
      existing ?? {
        id: uid(),
        street: 'preflop',
        actor: seat,
        type: 'fold',
      },
    )
  }

  {
    const existing = takeMatching(
      (a) =>
        a.type === 'call' &&
        actorsMatch(a.actor, callSeat, hand.heroPosition),
    )
    ordered.push(
      existing ?? {
        id: uid(),
        street: 'preflop',
        actor: callSeat,
        type: 'call',
      },
    )
  }

  return ordered
}

/** Prune knownHoles keys that are not current table seats (and drop hero seat). */
export function pruneKnownHoles(
  knownHoles: HandState['knownHoles'],
  playerCount: number,
  heroPosition: string,
): HandState['knownHoles'] {
  const positions = new Set(getPositions(playerCount))
  const next: HandState['knownHoles'] = {}
  for (const [pos, holes] of Object.entries(knownHoles ?? {})) {
    if (!positions.has(pos) || pos === heroPosition) continue
    if (!holes) continue
    if (!holes[0] && !holes[1]) continue
    next[pos] = [holes[0], holes[1]]
  }
  return next
}

export type KnownHoleEntry = {
  /** Display label e.g. "BTN" or "BB" (seat name only, no Hero) */
  label: string
  position: string
  isHero: boolean
  hole: [Card | null, Card | null]
}

/** Hero first (always listed when any card or always for formatting), then other seats with known cards. */
export function listKnownHoleEntries(
  state: HandState,
  opts?: { includeEmptyHero?: boolean },
): KnownHoleEntry[] {
  const includeEmptyHero = opts?.includeEmptyHero !== false
  const entries: KnownHoleEntry[] = []
  const heroHas = Boolean(state.holeCards[0] || state.holeCards[1])
  if (includeEmptyHero || heroHas) {
    entries.push({
      label: formatSeatCode(state.heroPosition, state.playerCount),
      position: state.heroPosition,
      isHero: true,
      hole: state.holeCards,
    })
  }
  for (const pos of getPositions(state.playerCount)) {
    if (pos === state.heroPosition) continue
    const holes = state.knownHoles?.[pos]
    if (!holes) continue
    if (!holes[0] && !holes[1]) continue
    entries.push({
      label: formatSeatCode(pos, state.playerCount),
      position: pos,
      isHero: false,
      hole: holes,
    })
  }
  return entries
}

/** Honest pot amount/shape labels for summaries, share, and meta. */
export function formatPotDisplayForHand(state: HandState): PotDisplayParts {
  // Softwise M10c: amount only — the HU / multiway label lives in one place
  // (potShape.shareProcessKind) and is never shown twice.
  return formatPotDisplay(state.potBb, t())
}

/** True when board cards for the street are incomplete / missing. */
export function streetBoardIncomplete(state: HandState, street: Street): boolean {
  if (street === 'preflop') return false
  if (street === 'flop') return state.board.flop.some((c) => !c)
  if (street === 'turn') return !state.board.turn
  if (street === 'river') return !state.board.river
  return false
}

/**
 * Soft warnings when street actions exist but board cards for that street
 * are missing. Non-blocking — never invents cards.
 */
export function boardMismatchWarnings(state: HandState): string[] {
  const ui = t()
  const warnings: string[] = []
  const hasFlopAct = state.actions.some((a) => a.street === 'flop')
  const hasTurnAct = state.actions.some((a) => a.street === 'turn')
  const hasRiverAct = state.actions.some((a) => a.street === 'river')
  const flopFilled = state.board.flop.filter(Boolean).length
  // Align with Actions CTA: empty flop OR partial flop (1–2 of 3) when flop actions exist
  if (hasFlopAct && flopFilled < 3) {
    warnings.push(flopFilled === 0 ? ui.missingFlopBoard : ui.boardNeedFlopPartial)
  }
  if (hasTurnAct && !state.board.turn) warnings.push(ui.missingTurnBoard)
  if (hasRiverAct && !state.board.river) warnings.push(ui.missingRiverBoard)
  return warnings
}

/** Message + CTA label for Actions board banner (street-scoped). */
export function boardStreetCta(
  state: HandState,
  street: Street,
): { message: string; cta: string } | null {
  if (street === 'preflop' || !streetBoardIncomplete(state, street)) return null
  const ui = t()
  if (street === 'flop') {
    const hasAct = state.actions.some((a) => a.street === 'flop')
    const flopFilled = state.board.flop.filter(Boolean).length
    // Distinct copy: fully empty vs partial (1–2 of 3)
    let message: string
    if (flopFilled === 0) {
      message = hasAct ? ui.missingFlopBoard : ui.boardNeedFlop
    } else {
      message = ui.boardNeedFlopPartial
    }
    return {
      message,
      cta: ui.boardCtaFlop,
    }
  }
  if (street === 'turn') {
    // Single card — empty vs present is enough (incomplete ⇒ empty)
    const hasAct = state.actions.some((a) => a.street === 'turn')
    return {
      message: hasAct ? ui.missingTurnBoard : ui.boardNeedTurn,
      cta: ui.boardCtaTurn,
    }
  }
  const hasAct = state.actions.some((a) => a.street === 'river')
  return {
    message: hasAct ? ui.missingRiverBoard : ui.boardNeedRiver,
    cta: ui.boardCtaRiver,
  }
}

/**
 * Multiway-ish Share context: table size ≥3. Used only for optional
 * opponent-hole CTAs — never soft-gate. (Softwise M10d: the old HU / N-way
 * pot-shape token is gone; table size alone decides.)
 */
export function isMultiwayIshShareContext(state: HandState): boolean {
  return state.playerCount >= 3
}

/**
 * Optional Share CTA into Hand / withVillains when hero-only, no villain holes,
 * and multiway-ish. Missing villain holes must never gate Share.
 * Softwise M10d: never offered when the hand ended with one player left —
 * no showdown, so opponents' cards are not asked.
 */
export function shareOffersOptionalVillainHolesCta(
  state: HandState,
  holesMode: 'heroOnly' | 'withVillains',
  hasVillainHoles: boolean,
): boolean {
  if (holesMode !== 'heroOnly' || hasVillainHoles) return false
  // Softwise M10d / Soft Quiet C2: no showdown ask after one-left or mid-cut.
  if (handEndsOnePlayerLeft(deriveHandFlow(state))) return false
  if (handEndsMemoryCut(deriveHandFlow(state))) return false
  return isMultiwayIshShareContext(state)
}

/** Pretty line for one hole pair, with optional shorthand. */
export function formatHolePrettyLine(
  hole: [Card | null, Card | null],
): string {
  const pretty = formatCards(hole)
  if (!pretty) return ''
  const pro = formatHolePro(hole)
  return pro.shorthand ? `${pretty} (${pro.shorthand})` : pretty
}

export function uid(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
}

const RANKS_SET = new Set<string>(RANKS)
const SUITS_SET = new Set<string>(SUITS)

export function isCard(value: unknown): value is Card {
  if (typeof value !== 'string' || value.length !== 2) return false
  return RANKS_SET.has(value[0]!) && SUITS_SET.has(value[1]!)
}

export function sanitizeHand(raw: unknown): HandState | null {
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  const base = createInitialState()

  const playerCount = Number(o.playerCount)
  if (Number.isFinite(playerCount)) {
    base.playerCount = Math.min(9, Math.max(2, Math.round(playerCount)))
  }

  const smallBlind = Number(o.smallBlind)
  if (Number.isFinite(smallBlind) && smallBlind >= 0) base.smallBlind = smallBlind

  const bigBlind = Number(o.bigBlind)
  if (Number.isFinite(bigBlind) && bigBlind >= 0) base.bigBlind = bigBlind

  const ante = Number(o.ante)
  if (Number.isFinite(ante) && ante >= 0) base.ante = ante

  const stackUnit = o.stackUnit === 'chips' ? 'chips' : 'bb'
  base.stackUnit = stackUnit

  const heroStackBb = Number(o.heroStackBb)
  if (Number.isFinite(heroStackBb) && heroStackBb >= 0) base.heroStackBb = heroStackBb

  const heroStackChips = Number(o.heroStackChips)
  if (Number.isFinite(heroStackChips) && heroStackChips >= 0) {
    base.heroStackChips = heroStackChips
  } else {
    base.heroStackChips = bbToChips(base.heroStackBb, base.bigBlind)
  }

  const effectiveStackBb = Number(o.effectiveStackBb)
  if (Number.isFinite(effectiveStackBb) && effectiveStackBb >= 0) {
    base.effectiveStackBb = effectiveStackBb
  }

  const potBb = Number(o.potBb)
  if (Number.isFinite(potBb) && potBb >= 0) {
    base.potBb = potBb
  }

  const positions = getPositions(base.playerCount)
  const heroPosition = typeof o.heroPosition === 'string' ? o.heroPosition : ''
  base.heroPosition = positions.includes(heroPosition)
    ? heroPosition
    : positions[positions.length - 1]!

  if (Array.isArray(o.holeCards)) {
    base.holeCards = [
      isCard(o.holeCards[0]) ? o.holeCards[0] : null,
      isCard(o.holeCards[1]) ? o.holeCards[1] : null,
    ]
  }

  const board = o.board
  if (board && typeof board === 'object') {
    const b = board as Record<string, unknown>
    const flop = Array.isArray(b.flop) ? b.flop : []
    base.board = {
      flop: [
        isCard(flop[0]) ? flop[0] : null,
        isCard(flop[1]) ? flop[1] : null,
        isCard(flop[2]) ? flop[2] : null,
      ],
      turn: isCard(b.turn) ? b.turn : null,
      river: isCard(b.river) ? b.river : null,
    }
  }

  // Parse knownHoles (legacy saves may omit the field)
  base.knownHoles = {}
  const rawKnown = o.knownHoles
  if (rawKnown && typeof rawKnown === 'object' && !Array.isArray(rawKnown)) {
    const kh = rawKnown as Record<string, unknown>
    for (const [pos, holes] of Object.entries(kh)) {
      if (!positions.includes(pos) || pos === base.heroPosition) continue
      if (!Array.isArray(holes)) continue
      base.knownHoles[pos] = [
        isCard(holes[0]) ? holes[0] : null,
        isCard(holes[1]) ? holes[1] : null,
      ]
    }
  }

  const seen = new Set<Card>()
  const claim = (c: Card | null): Card | null => {
    if (!c) return null
    if (seen.has(c)) return null
    seen.add(c)
    return c
  }
  base.holeCards = [claim(base.holeCards[0]), claim(base.holeCards[1])]
  const prunedKnown: HandState['knownHoles'] = {}
  for (const [pos, holes] of Object.entries(base.knownHoles)) {
    if (!holes) continue
    const pair: [Card | null, Card | null] = [claim(holes[0]), claim(holes[1])]
    if (pair[0] || pair[1]) prunedKnown[pos] = pair
  }
  base.knownHoles = prunedKnown
  base.board.flop = [
    claim(base.board.flop[0]),
    claim(base.board.flop[1]),
    claim(base.board.flop[2]),
  ]
  base.board.turn = claim(base.board.turn)
  base.board.river = claim(base.board.river)

  if (Array.isArray(o.actions)) {
    const streets: Street[] = ['preflop', 'flop', 'turn', 'river']
    const types = new Set(['fold', 'check', 'call', 'bet', 'raise', 'allin'])
    base.actions = o.actions
      .filter((a): a is Record<string, unknown> => !!a && typeof a === 'object')
      .map((a) => {
        const street = streets.includes(a.street as Street)
          ? (a.street as Street)
          : 'preflop'
        const type = types.has(String(a.type))
          ? (a.type as HandState['actions'][number]['type'])
          : 'check'
        const size =
          typeof a.size === 'string' && a.size.trim() ? a.size.trim() : undefined
        return {
          id: typeof a.id === 'string' && a.id ? a.id : uid(),
          street,
          actor: typeof a.actor === 'string' && a.actor.trim() ? a.actor.trim() : 'Hero',
          type,
          size:
            type === 'bet' || type === 'raise' || type === 'allin' ? size : undefined,
        }
      })
      .slice(0, 80)
  }

  if (typeof o.takeaway === 'string') {
    base.takeaway = o.takeaway.replace(/[\r\n]+/g, ' ').slice(0, 160)
  }

  if (Array.isArray(o.unrecordedSeats)) {
    const seats = o.unrecordedSeats
      .filter((s): s is string => typeof s === 'string' && positions.includes(s))
      .filter((s) => s !== base.heroPosition)
    if (seats.length) base.unrecordedSeats = [...new Set(seats)]
  }

  // Soft Quiet C2: restore voluntary mid-cut (honest incomplete).
  const streets: Street[] = ['preflop', 'flop', 'turn', 'river']
  if (typeof o.memoryCutAt === 'string' && streets.includes(o.memoryCutAt as Street)) {
    base.memoryCutAt = o.memoryCutAt as Street
  }

  // Soft G: optional ending record — only when the user pressed something
  // (absent otherwise, so empty-ending state stays byte-identical).
  const ending = sanitizeEnding(o.ending, positions)
  if (ending) base.ending = ending

  return base
}
